import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Center,
  Flex,
  Image,
  Button,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  InputGroup,
  IconButton,
  useToast,
  Spinner,
} from '@chakra-ui/react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import dominus_logo_transparent_white from '../assets/dominus_logo_transparent_white.png';
import VisitorFooter from '../components/VisitorFooter';
import GlassCard from '../components/GlassCard';
import apiClient from '../lib/api/apiClient';
import { authEndpoints, getAuthEndpoint } from '../lib/api/authApiClient';
import NotFoundPage from './NotFoundPage';

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = searchParams.get('token') || '';

  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Password validation helper (same as RegistrationForm)
  function validatePassword(pw: string) {
    if (!pw) return '';
    const errs = [];
    if (pw.length < 8) errs.push('• At least 8 characters');
    if (!/[A-Z]/.test(pw)) errs.push('• At least one uppercase letter');
    if (!/[a-z]/.test(pw)) errs.push('• At least one lowercase letter');
    if (!/[0-9]/.test(pw)) errs.push('• At least one number');
    if (!/[^A-Za-z0-9]/.test(pw)) errs.push('• At least one special character');
    return errs.length ? errs.join('\n') : '';
  }

  const passwordValidationError = validatePassword(password);

  // Validate reset token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidToken(false);
        setIsValidating(false);
        return;
      }
      try {
        const response = await apiClient.post('/user/reset-token', { token });
        setUserEmail(response.data.email);
        setIsValidToken(true);
      } catch {
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (passwordValidationError) {
      newErrors.password = passwordValidationError;
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setIsSubmitting(true);
      const response = await fetch(authEndpoints.forgotPassword(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, email: userEmail }),
      });

      const data = await response.json();

      if (!response.ok || data.error) {
        toast({
          title: 'Reset Failed',
          description: data.message || 'Unable to reset password. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      await apiClient.delete(`/user/reset-token/${token}`);
      toast({
        title: 'Password Reset',
        description: 'Your password has been reset successfully. Please sign in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      navigate('/signin');
    } catch {
      toast({
        title: 'Request Failed',
        description: 'Unable to reset password. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state while validating token
  if (isValidating) {
    return (
      <Box
        minH="100vh"
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="absolute"
        top="0"
        left="0"
        bgGradient="linear(to-b, black, #002e0b)"
      >
        <Spinner size="xl" color="white" thickness="4px" />
      </Box>
    );
  }

  // Invalid or missing token
  if (!isValidToken) {
    return <NotFoundPage />;
  }

  const isFormValid = !passwordValidationError && password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      flexDirection="column"
      position="absolute"
      top="0"
      left="0"
      bgGradient="linear(to-b, black, #002e0b)"
      color="white"
      fontFamily="'Exo 2', sans-serif"
    >
      {/* Navigation Bar */}
      <Box
        as="nav"
        position="fixed"
        top={0}
        w="100%"
        bg="transparent"
        backdropFilter="blur(8px)"
        zIndex={50}
      >
        <Flex justifyContent="space-between" maxW="8xl" mx="auto" px={4} py={4}>
          <Box flexShrink={0}>
            <Link to="/">
              <Image src={dominus_logo_transparent_white} alt="Dominus" h={12} />
            </Link>
          </Box>
          <Box>
            <Button
              onClick={() => navigate('/signin')}
              variant="dominus"
            >
              Sign In
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Reset Password Content */}
      <Container maxW="600px" pt={32} flex="1 0 auto">
        <Center minH="60vh">
          <VStack gap={8} width="full">
            <VStack gap={3} textAlign="center" w="full">
              <Box w="full" maxW="600px" mx="auto">
                <Heading
                  as="h1"
                  size="xl"
                  fontFamily="Audiowide"
                  fontWeight="bold"
                  color="white"
                >
                  Reset Your Password
                </Heading>
              </Box>
            </VStack>

            <GlassCard p={8} w="full" textAlign="center" baseColor="#002e0b">
              <form onSubmit={handleSubmit}>
                <VStack gap={5}>
                  <Text color="whiteAlpha.800" fontSize="sm">
                    Enter your new password below.
                  </Text>

                  <FormControl isRequired isInvalid={!!(passwordTouched && passwordValidationError)}>
                    <FormLabel color="white">New Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          setPasswordTouched(true);
                          if (errors.password) setErrors({ ...errors, password: '' });
                        }}
                        onBlur={() => setPasswordTouched(true)}
                        bg="transparent"
                        color="white"
                        borderColor="whiteAlpha.400"
                        borderWidth="1.5px"
                        _hover={{ borderColor: 'whiteAlpha.600', bg: 'transparent' }}
                        _focus={{ borderColor: 'white', bg: 'transparent' }}
                        disabled={isSubmitting}
                      />
                      <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          size="sm"
                          variant="ghost"
                          bg="transparent"
                          backgroundColor="transparent"
                          color="white"
                          _hover={{ bg: 'transparent', backgroundColor: 'transparent', color: 'gray.200' }}
                          _active={{ bg: 'transparent', backgroundColor: 'transparent' }}
                          _focus={{ boxShadow: 'none' }}
                          onClick={() => setShowPassword((s) => !s)}
                          tabIndex={-1}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </IconButton>
                      </Box>
                    </InputGroup>
                    {passwordTouched && passwordValidationError && (
                      <FormErrorMessage>
                        <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                          {passwordValidationError.split('\n').map((line, idx) => (
                            <li key={idx} style={{ textAlign: "left" }}>{line}</li>
                          ))}
                        </ul>
                      </FormErrorMessage>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.confirmPassword}>
                    <FormLabel color="white">Confirm Password</FormLabel>
                    <InputGroup>
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                        }}
                        bg="transparent"
                        color="white"
                        borderColor="whiteAlpha.400"
                        borderWidth="1.5px"
                        _hover={{ borderColor: 'whiteAlpha.600', bg: 'transparent' }}
                        _focus={{ borderColor: 'white', bg: 'transparent' }}
                        disabled={isSubmitting}
                      />
                      <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                        <IconButton
                          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                          size="sm"
                          variant="ghost"
                          bg="transparent"
                          backgroundColor="transparent"
                          color="white"
                          _hover={{ bg: 'transparent', backgroundColor: 'transparent', color: 'gray.200' }}
                          _active={{ bg: 'transparent', backgroundColor: 'transparent' }}
                          _focus={{ boxShadow: 'none' }}
                          onClick={() => setShowConfirmPassword((s) => !s)}
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                        </IconButton>
                      </Box>
                    </InputGroup>
                    {errors.confirmPassword && <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>}
                  </FormControl>

                  <Box w="full">
                    <Button
                      type="submit"
                      w="full"
                      variant="dominus"
                      isLoading={isSubmitting}
                      isDisabled={!isFormValid || isSubmitting}
                    >
                      Reset Password
                    </Button>
                  </Box>
                </VStack>
              </form>
            </GlassCard>
          </VStack>
        </Center>
      </Container>
      <VisitorFooter />
    </Box>
  );
};

export default ForgotPasswordPage;
